import { motion } from 'framer-motion';
import React from 'react';

interface AnimatedTextProps {
  text: string;
  className?: string;
  wordDelay?: number;
  animationType?: 'slideInFromLeft' | 'fadeIn' | 'slideInFromTop' | 'slideInFromLeftTop' | 'fadeInScale';
  autoPlay?: boolean;
  isInView?: boolean;
  delayChildren?: number;
}
export const AnimatedText = ({
  text,
  wordDelay = 0.1,
  delayChildren = 0.2,
  animationType = 'fadeIn',
  className = '',
  isInView = true,
}: AnimatedTextProps) => {
  const normalizedText = text.replace(/\\n/g, '\n');
  const lines = normalizedText.split(/\r\n|\r|\n/);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: wordDelay,
        delayChildren,
      },
    },
  };

  const wordVariants = {
    slideInFromLeft: {
      hidden: {
        x: -100,
        opacity: 0,
      },
      visible: {
        x: 0,
        opacity: 1,
        transition: {
          type: 'spring',
          damping: 12,
          stiffness: 100,
        },
      },
    },
    slideInFromTop: {
      hidden: {
        y: -50,
        opacity: 0,
      },
      visible: {
        y: 0,
        opacity: 1,
        transition: {
          type: 'spring',
          damping: 12,
          stiffness: 100,
        },
      },
    },
    slideInFromLeftTop: {
      hidden: {
        x: -50,
        y: -30,
        opacity: 0,
        scale: 0.8,
      },
      visible: {
        x: 0,
        y: 0,
        opacity: 1,
        scale: 1,
        transition: {
          type: 'spring',
          damping: 10,
          stiffness: 100,
        },
      },
    },
    fadeInScale: {
      hidden: {
        opacity: 0,
        scale: 0.5,
      },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          type: 'spring',
          damping: 10,
          stiffness: 100,
        },
      },
    },
    fadeIn: {
      hidden: {
        opacity: 0,
      },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          type: 'spring',
          damping: 20,
          stiffness: 100,
          duration: 0.8,
        },
      },
    },
  };

  return (
    <div className='text-center space-y-6'>
      <motion.div
        variants={containerVariants}
        initial='hidden'
        animate={isInView ? 'visible' : 'hidden'}
        viewport={{ once: false }}
        className={`${className}`}
      >
        {lines.map((line, lineIndex) => (
          <div key={lineIndex}>
            {line.split(' ').map((word, wordIndex) => (
              <motion.span
                key={`${lineIndex}-${wordIndex}`}
                variants={wordVariants[animationType]}
                className='inline-block mr-2'
              >
                {word}
              </motion.span>
            ))}
          </div>
        ))}
      </motion.div>
    </div>
  );
};
